import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminFirmDashboard.module.css';
import { firmApi } from '../../services/firmApi';
import { FirmDetailed } from '../../types/firm';

const AdminFirmDashboard: React.FC = () => {
  const [firms, setFirms] = useState<FirmDetailed[]>([]);
  const [filteredFirms, setFilteredFirms] = useState<FirmDetailed[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  // Simple effect to fetch firms on mount
  useEffect(() => {
    let isComponentMounted = true; // Local variable to track mount state

    // Fetch firms on component mount
    const fetchFirms = async () => {
      try {
        console.log('Starting to fetch firms...');
        // Show loading state
        setLoading(true);
        
        const data = await firmApi.getAllFirms();
        console.log('Fetched firms:', data); // Add logging to debug
        console.log('Firms array length:', data.length);
        
        // Check if data is valid array
        if (!Array.isArray(data)) {
          console.error('API did not return an array:', data);
          toast.error('Invalid data format received from the server', {
            position: "top-right",
            autoClose: 5000
          });
          if (isComponentMounted) {
            setLoading(false);
          }
          return;
        }
        
        // Make sure component is still mounted before updating state
        if (isComponentMounted) {
          console.log('Component is mounted, updating state with', data.length, 'firms');
          
          // Update state in sequence with setTimeout to avoid React batching updates
          setFirms(data);
          
          setTimeout(() => {
            if (isComponentMounted) {
              setFilteredFirms(data);
              console.log('Filtered firms updated, setting loading to false');
              setLoading(false);
            }
          }, 0);
        }
      } catch (error) {
        console.error('Error fetching firms:', error);
        toast.error('Failed to load firms. Please try again later.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        if (isComponentMounted) {
          setLoading(false);
        }
      }
    };

    // Execute the fetch function
    console.log('Calling fetchFirms...');
    fetchFirms();

    // Cleanup function to set the local variable when the component unmounts
    return () => {
      console.log('Component unmounting, cleaning up');
      isComponentMounted = false;
      isMountedRef.current = false;
    };
  }, []);

  // Force loading to false if we have firms but loading is still true
  useEffect(() => {
    if (firms.length > 0 && loading) {
      console.log('Firms loaded but loading state is still true, forcing loading to false');
      setLoading(false);
    }
  }, [firms, loading]);

  useEffect(() => {
    // Filter firms when search term changes
    console.log('Filtering firms with term:', searchTerm, 'from', firms.length, 'firms');
    
    if (searchTerm) {
      const filtered = firms.filter(firm =>
        (firm.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firm.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firm.url || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('Filtered results:', filtered.length, 'firms');
      setFilteredFirms(filtered);
    } else {
      console.log('No search term, setting all firms:', firms.length);
      setFilteredFirms(firms);
    }
  }, [searchTerm, firms]);

  const handleDeleteFirm = async (firmId: string) => {
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this firm?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await firmApi.deleteFirm(firmId);
                // Remove the firm from state
                setFirms(prevFirms => prevFirms.filter(firm => String(firm.Firm_ID) !== firmId));
                setFilteredFirms(prevFiltered => prevFiltered.filter(firm => String(firm.Firm_ID) !== firmId));
                toast.success('Firm deleted successfully', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error) {
                console.error('Error deleting firm:', error);
                toast.error('Failed to delete firm. Please try again later.', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Toast Container with custom settings */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
          </li>
          <li className="breadcrumb-item active">Firm Management</li>
        </ol>
      </nav>

        {/* Search and Create Button */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className={styles.searchContainer}>
            <input
              id="search-input"
              type="text"
              className={`form-control ${styles.searchInput}`}
              placeholder="Search firm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Link className="btn btn-primary" to="/admin/firms/create">
            Create new Firm
          </Link>
        </div>

        {/* Firms Table */}
        <div id="firm-list-container" className={styles.firmListContainer}>
          <div className={`${styles.mainBox} ${styles.clearfix}`}>
            <div className="table-responsive">
              <table className={`table ${styles.table}`}>
                <thead>
                  <tr>
                    <th className={styles.tableHeaderCell}>Firm</th>
                    <th className={styles.tableHeaderCell}>Abbrev</th>
                    <th className={styles.tableHeaderCell}>URL</th>
                    <th className={styles.tableHeaderCell}>Created</th>
                    <th className={styles.tableHeaderCell}>Updated</th>
                    <th className={styles.tableHeaderCell}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">Loading firms...</div>
                      </td>
                    </tr>
                  ) : filteredFirms.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        {firms.length > 0 ? 
                          'No firms match your search criteria' : 
                          'No firms found. Please check API connection.'}
                      </td>
                    </tr>
                  ) : (
                    filteredFirms.map((firm) => (
                      <tr key={firm.Firm_ID}>
                        <td className={styles.tableCell}>
                          <div className={styles.firmInfo}>
                            <div className={styles.firmLogoCell}>
                              {firm.logo ? (
                                <img
                                  src={`data:image/png;base64,${firm.logo}`}
                                  alt={`${firm.name} logo`}
                                  className={styles.firmLogo}
                                />
                              ) : (
                                <div className={styles.firmLogo} style={{ backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="bi bi-building text-secondary"></i>
                                </div>
                              )}
                            </div>
                            <div className={styles.firmDetails}>
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/admin/firms/${String(firm.Firm_ID)}`);
                                }} 
                                className={styles.firmLink}
                              >
                                {firm.name}
                              </a>
                              <div className={styles.firmAbbrevCell}>{firm.abbrev}</div>
                            </div>
                          </div>
                        </td>
                        <td className={styles.tableCell}>{firm.abbrev}</td>
                        <td className={styles.tableCell}>
                          {firm.url && (
                            <a href={firm.url} target="_blank" rel="noopener noreferrer">
                              {firm.url}
                            </a>
                          )}
                        </td>
                        <td className={styles.tableCell}>{formatDate(firm.created_at)}</td>
                        <td className={styles.tableCell}>{formatDate(firm.updated_at)}</td>
                        <td className={`text-end ${styles.tableCell}`}>
                          <div className={`btn-group ${styles.actionButtonsContainer}`}>
                            <button 
                              className={`btn btn-sm btn-outline-primary ${styles.actionButton}`} 
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/admin/firms/${String(firm.Firm_ID)}`);
                              }}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button 
                              className={`btn btn-sm btn-outline-danger ${styles.actionButton}`} 
                              onClick={() => handleDeleteFirm(String(firm.Firm_ID))}
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
 
  );
};

export default AdminFirmDashboard;
